import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, TrendingUp, Shield, Award } from 'lucide-react';

const Guides = () => {
  const guides = [
    {
      id: 1,
      slug: 'getting-started-online-casinos',
      title: 'Getting Started with Online Casinos',
      description: 'Everything you need to know to begin your online casino journey in the UK, from account creation to making your first deposit.'
    },
    {
      id: 2,
      slug: 'understanding-welcome-bonuses',
      title: 'Understanding Welcome Bonuses',
      description: 'Decode bonus terms, maximize your welcome package value, and learn which bonuses actually benefit players.'
    },
    {
      id: 3,
      slug: 'choosing-right-casino',
      title: 'Choosing the Right Casino',
      description: 'Learn what separates trustworthy operators from the rest. Licensing, security, game selection, and player protection standards.'
    },
    {
      id: 4,
      slug: 'payment-methods-withdrawals',
      title: 'Payment Methods and Withdrawals',
      description: 'Navigate deposit options, withdrawal times, and payment security. Get your winnings faster with the right payment method.'
    },
    {
      id: 5,
      slug: 'responsible-gambling',
      title: 'Responsible Gambling',
      description: 'Essential tools and strategies for maintaining control. Set limits, recognize warning signs, and gamble safely.'
    },
    {
      id: 6,
      slug: 'understanding-wagering-requirements',
      title: 'Understanding Wagering Requirements',
      description: 'Master bonus terms and conditions. Learn how wagering requirements work and which bonuses offer real value.'
    },
    {
      id: 7,
      slug: 'live-dealer-games-guide',
      title: 'Live Dealer Games Guide',
      description: 'Experience authentic casino action from home. Learn how live dealer games work and which tables offer the best experience.'
    },
    {
      id: 8,
      slug: 'slot-games-strategy',
      title: 'Slot Games Strategy',
      description: 'Maximize your slot sessions with smart bankroll management, RTP knowledge, and game selection strategies.'
    },
    {
      id: 9,
      slug: 'table-games-basics',
      title: 'Table Games Basics',
      description: 'Master blackjack, roulette, and baccarat fundamentals. Learn basic strategies and table etiquette for UK casinos.'
    }
  ];

  return (
    <main className="min-h-screen py-20 bg-dark-black">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              How to Play Online Casinos in the UK
            </h1>
            <p className="text-gray-400 text-xl max-w-3xl mx-auto">
              Expert guides crafted to help you navigate the UK online casino landscape with confidence and knowledge.
            </p>
          </div>

          {/* About Our Guides Section */}
          <div className="bg-gradient-to-br from-gray-900 to-dark-black border border-gray-800 rounded-2xl p-8 md:p-12 mb-16">
            <div className="flex items-start gap-6 mb-8">
              <div className="bg-toxic-orange/20 p-4 rounded-lg">
                <BookOpen className="text-toxic-orange" size={40} />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">
                  How We Write Our Guides
                </h2>
              </div>
            </div>
            
            <div className="space-y-6 text-gray-300 leading-relaxed">
              <p>
                Our guides aren't generic templates filled with industry jargon. Every piece of content we publish comes from real experience—hours spent testing casinos, analyzing bonus terms, and talking to players about what actually matters. We've seen too many guides that read like marketing copy, promising the world without delivering substance. That's not how we operate.
              </p>
              
              <p>
                When we write about welcome bonuses, we don't just list the biggest numbers. We break down the wagering requirements, explain how they impact your chances of actually withdrawing winnings, and compare offers side-by-side so you can see which bonuses deliver real value versus which ones look impressive but fall apart under scrutiny. We test withdrawal processes ourselves, noting which casinos process payments in hours versus days, and which payment methods work best for UK players.
              </p>
              
              <p>
                Our casino selection guides focus on what players genuinely care about: licensing and regulation, game variety from trusted providers, customer service responsiveness, and fair bonus terms. We don't recommend casinos based on affiliate commissions—we recommend them because they've proven themselves reliable, secure, and player-friendly over time. Every casino we feature has been vetted for UK Gambling Commission licensing, SSL encryption, responsible gambling tools, and positive player feedback.
              </p>
              
              <p>
                The strategies we share aren't theoretical. They're practical approaches that work in real casino environments. Whether you're learning basic blackjack strategy or understanding how RTP affects your slot sessions, we explain concepts clearly without dumbing them down. We respect your intelligence and your time, which means no fluff, no filler, and no false promises about guaranteed wins.
              </p>
              
              <p>
                Our responsible gambling content goes beyond boilerplate warnings. We explain how to set realistic deposit limits, recognize when gambling stops being entertainment, and use the tools available to maintain control. We're honest about the risks because informed players make better decisions. Every guide includes practical steps you can take immediately, not just abstract advice.
              </p>
              
              <p>
                We update our guides regularly because the UK casino market evolves constantly. New regulations change how casinos operate. Payment methods come and go. Bonus terms shift. What was accurate six months ago might be outdated today. That's why we review and refresh our content, ensuring you always have current, actionable information.
              </p>
              
              <p>
                Transparency matters to us. If we have affiliate relationships with casinos we recommend, we're upfront about it. But those relationships never influence our editorial content. A casino earns our recommendation through merit, not marketing budgets. We've turned down lucrative affiliate deals from operators that didn't meet our standards because credibility is worth more than commissions.
              </p>
              
              <p>
                Our goal is simple: give you the knowledge you need to make smart choices, avoid common pitfalls, and get maximum value from your casino experience. Whether you're a complete beginner or someone looking to refine their approach, our guides provide the insights that matter. No hype, no sales pitches—just honest, practical guidance from people who understand the industry inside and out.
              </p>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {/* Chart 1 */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="text-toxic-orange" size={24} />
                <h3 className="text-white font-semibold">UK Casino Growth</h3>
              </div>
              <div className="h-48 bg-dark-black rounded-lg flex items-end justify-center gap-2 p-4 border border-gray-700">
                {/* Chart bars placeholder */}
                <div className="w-8 bg-toxic-orange rounded-t" style={{ height: '60%' }}></div>
                <div className="w-8 bg-toxic-orange rounded-t" style={{ height: '75%' }}></div>
                <div className="w-8 bg-toxic-orange rounded-t" style={{ height: '90%' }}></div>
                <div className="w-8 bg-toxic-orange rounded-t" style={{ height: '100%' }}></div>
                <div className="w-8 bg-toxic-orange rounded-t" style={{ height: '85%' }}></div>
              </div>
              <p className="text-gray-400 text-sm mt-4 text-center">
                Online casino market growth in UK (2020-2024)
              </p>
            </div>

           

            {/* Chart 3 */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Award className="text-toxic-orange" size={24} />
                <h3 className="text-white font-semibold">Player Satisfaction</h3>
              </div>
              <div className="h-48 bg-dark-black rounded-lg flex flex-col justify-center gap-3 p-4 border border-gray-700">
                {/* Bar chart placeholder */}
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-xs w-20">Security</span>
                  <div className="flex-1 bg-gray-800 rounded-full h-4">
                    <div className="bg-toxic-orange h-4 rounded-full" style={{ width: '95%' }}></div>
                  </div>
                  <span className="text-gray-400 text-xs">95%</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-xs w-20">Payouts</span>
                  <div className="flex-1 bg-gray-800 rounded-full h-4">
                    <div className="bg-toxic-orange h-4 rounded-full" style={{ width: '88%' }}></div>
                  </div>
                  <span className="text-gray-400 text-xs">88%</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-xs w-20">Games</span>
                  <div className="flex-1 bg-gray-800 rounded-full h-4">
                    <div className="bg-toxic-orange h-4 rounded-full" style={{ width: '92%' }}></div>
                  </div>
                  <span className="text-gray-400 text-xs">92%</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-xs w-20">Support</span>
                  <div className="flex-1 bg-gray-800 rounded-full h-4">
                    <div className="bg-toxic-orange h-4 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                  <span className="text-gray-400 text-xs">85%</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm mt-4 text-center">
                Average player satisfaction ratings across UK casinos
              </p>
            </div>
          </div>

          {/* Guides Grid */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Complete Guide Collection
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {guides.map((guide, index) => (
                <Link
                  key={guide.id}
                  to={`/guides/${guide.slug}`}
                  data-discover="true"
                  className="bg-gradient-to-br from-gray-900 to-dark-black border border-gray-800 rounded-xl p-6 hover:border-toxic-orange transition-all duration-300 transform hover:-translate-y-1 group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className="text-toxic-orange text-sm font-semibold">
                      Guide #{index + 1}
                    </span>
                    <ArrowRight className="text-gray-400 group-hover:text-toxic-orange group-hover:translate-x-1 transition-all" size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-toxic-orange transition-colors">
                    {guide.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {guide.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Guides;
